import { Logo, TrendChart, color, space, status } from '@biopeak/ui';
import type { ReportContent } from '../lib/report.ts';

/**
 * O documento em si — o que vira PDF.
 *
 * Só apresentação: recebe conteúdo pronto e desenha. Nenhuma decisão clínica,
 * nenhum cálculo, nenhuma chamada de rede. Isso importa porque este mesmo
 * componente vai ser servido por link no envio por e-mail depois; quanto menos
 * ele souber de onde o dado veio, menos precisa mudar naquele momento.
 *
 * A classe `relatorio-impresso` é o que o CSS de impressão usa para esconder
 * todo o resto do painel (ver global.css).
 */

export interface DadosGrafico {
  titulo: string;
  unidade: string;
  /** Formato do TrendChart: um ponto por dia, com faixa e média. */
  pontos: { bucket: string; min: number; max: number; avg: number }[];
}

export interface NumeroDestaque {
  rotulo: string;
  valor: string;
  legenda: string;
}

export interface ExameResumo {
  nome: string;
  valor: string;
  unidade: string;
  referencia: string | null;
  foraDaFaixa: boolean;
}

export interface ReportDocumentProps {
  content: ReportContent;
  doctorMessage: string;
  paciente: { nome: string; nascimento: string | null };
  medico: string;
  periodoDias: number;
  geradoEm: string;
  graficos: DadosGrafico[];
  numeros: NumeroDestaque[];
  exames: ExameResumo[];
  dataExame: string | null;
}

interface Tom {
  fg: string;
  bg: string;
  border: string;
  rotulo: string;
}

/** Fallback é `estavel`: direção nova no schema degrada para neutro em vez de
    quebrar o documento na frente do paciente. */
const TOM_ESTAVEL: Tom = {
  fg: color.textSecondary,
  bg: 'transparent',
  border: color.border,
  rotulo: 'estável',
};

const TOM_DIRECAO: Record<string, Tom> = {
  melhora: { ...status.green, rotulo: 'melhorou' },
  piora: { ...status.red, rotulo: 'piorou' },
  atencao: { ...status.yellow, rotulo: 'merece atenção' },
  estavel: TOM_ESTAVEL,
};

export function ReportDocument({
  content,
  doctorMessage,
  paciente,
  medico,
  periodoDias,
  geradoEm,
  graficos,
  numeros,
  exames,
  dataExame,
}: ReportDocumentProps) {
  return (
    <div
      className="relatorio-impresso"
      style={{
        background: color.white,
        color: color.text,
        maxWidth: '820px',
        margin: '0 auto',
        padding: space.lg,
        fontSize: '14px',
        lineHeight: 1.6,
      }}
    >
      <Cabecalho paciente={paciente} medico={medico} periodoDias={periodoDias} geradoEm={geradoEm} />

      {doctorMessage.trim() && (
        <section
          className="quebra-evitar"
          style={{
            borderLeft: `3px solid ${color.accent}`,
            paddingLeft: space.md,
            margin: `${space.lg} 0`,
          }}
        >
          <p style={{ margin: 0, fontSize: '15px', fontStyle: 'italic' }}>
            {doctorMessage}
          </p>
          <p style={{ margin: `${space.xs} 0 0`, fontSize: '13px', color: color.textSecondary }}>
            — {medico}
          </p>
        </section>
      )}

      <section style={{ margin: `${space.lg} 0` }}>
        <p style={{ margin: `0 0 ${space.sm}`, fontSize: '16px' }}>{content.saudacao}</p>
        <p style={{ margin: 0, fontSize: '15px' }}>{content.resumo}</p>
      </section>

      {numeros.length > 0 && <NumerosChave numeros={numeros} />}

      {content.destaques.length > 0 && (
        <section style={{ margin: `${space.lg} 0` }}>
          <Titulo>O que aconteceu neste período</Titulo>
          {content.destaques.map((d, i) => (
            <Destaque key={i} destaque={d} />
          ))}
        </section>
      )}

      {graficos.length > 0 && <Graficos graficos={graficos} />}

      {exames.length > 0 && <Exames exames={exames} dataExame={dataExame} />}

      {content.habitos.length > 0 && (
        <section className="quebra-evitar" style={{ margin: `${space.lg} 0` }}>
          <Titulo>Hábitos que ajudam</Titulo>
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            {content.habitos.map((h, i) => (
              <li key={i} style={{ marginBottom: space.xs }}>{h}</li>
            ))}
          </ul>
        </section>
      )}

      {content.proxima_conversa.length > 0 && (
        <section
          className="quebra-evitar"
          style={{
            margin: `${space.lg} 0`,
            background: color.surface,
            borderRadius: '10px',
            padding: space.md,
          }}
        >
          <Titulo>Para conversar na próxima consulta</Titulo>
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            {content.proxima_conversa.map((p, i) => (
              <li key={i} style={{ marginBottom: space.xs }}>{p}</li>
            ))}
          </ul>
        </section>
      )}

      <Rodape fechamento={content.fechamento} geradoEm={geradoEm} />
    </div>
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: '17px',
        fontWeight: 600,
        margin: `0 0 ${space.sm}`,
        paddingBottom: space.xs,
        borderBottom: `1px solid ${color.border}`,
      }}
    >
      {children}
    </h2>
  );
}

function Cabecalho({
  paciente,
  medico,
  periodoDias,
  geradoEm,
}: {
  paciente: { nome: string; nascimento: string | null };
  medico: string;
  periodoDias: number;
  geradoEm: string;
}) {
  return (
    <header
      className="quebra-evitar"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: space.md,
        paddingBottom: space.md,
        borderBottom: `2px solid ${color.accent}`,
      }}
    >
      <div>
        <Logo variant="lockup" size={26} />
        <h1 style={{ fontSize: '20px', fontWeight: 600, margin: `${space.sm} 0 2px` }}>
          Relatório de acompanhamento
        </h1>
        <p style={{ margin: 0, fontSize: '13px', color: color.textSecondary }}>
          Últimos {periodoDias} dias · {medico}
        </p>
      </div>
      <div style={{ textAlign: 'right', fontSize: '13px', color: color.textSecondary }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: color.text }}>
          {paciente.nome}
        </div>
        {paciente.nascimento && <div>Nascimento: {paciente.nascimento}</div>}
        <div>Emitido em {geradoEm}</div>
      </div>
    </header>
  );
}

function NumerosChave({ numeros }: { numeros: NumeroDestaque[] }) {
  return (
    <section
      className="quebra-evitar"
      style={{
        display: 'flex',
        gap: space.md,
        flexWrap: 'wrap',
        margin: `${space.lg} 0`,
        padding: space.md,
        background: color.surface,
        borderRadius: '10px',
      }}
    >
      {numeros.map((n) => (
        <div key={n.rotulo} style={{ flex: '1 1 120px', minWidth: '120px' }}>
          <div style={{ fontSize: '12px', color: color.textSecondary }}>{n.rotulo}</div>
          <div style={{ fontSize: '24px', fontWeight: 600, lineHeight: 1.2 }}>{n.valor}</div>
          <div style={{ fontSize: '11px', color: color.textSecondary }}>{n.legenda}</div>
        </div>
      ))}
    </section>
  );
}

function Destaque({ destaque }: { destaque: ReportContent['destaques'][number] }) {
  const tom = TOM_DIRECAO[destaque.direcao] ?? TOM_ESTAVEL;

  return (
    <div
      className="quebra-evitar"
      style={{
        borderLeft: `3px solid ${tom.border}`,
        background: tom.bg,
        borderRadius: '4px',
        padding: `${space.sm} ${space.md}`,
        marginBottom: space.sm,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.sm, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '15px', fontWeight: 600 }}>{destaque.titulo}</span>
        <span style={{ fontSize: '12px', color: tom.fg }}>{tom.rotulo}</span>
      </div>
      <p style={{ margin: `2px 0 0`, fontSize: '14px' }}>{destaque.texto}</p>
    </div>
  );
}

function Graficos({ graficos }: { graficos: DadosGrafico[] }) {
  return (
    <section style={{ margin: `${space.lg} 0` }}>
      <Titulo>Suas medidas ao longo do período</Titulo>
      {graficos.map((g) => (
        <div key={g.titulo} className="quebra-evitar" style={{ marginBottom: space.lg }}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: space.xs }}>
            {g.titulo}
            <span style={{ fontWeight: 400, color: color.textSecondary }}> ({g.unidade})</span>
          </div>
          <TrendChart points={g.pontos} unit={g.unidade} height={120} />
        </div>
      ))}
    </section>
  );
}

function Exames({
  exames,
  dataExame,
}: {
  exames: ExameResumo[];
  dataExame: string | null;
}) {
  return (
    <section style={{ margin: `${space.lg} 0` }}>
      <Titulo>Seus exames{dataExame ? ` — coleta de ${dataExame}` : ''}</Titulo>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: color.textSecondary }}>
            <th style={{ padding: `${space.xs} 0`, fontWeight: 500 }}>Exame</th>
            <th style={{ padding: `${space.xs} 0`, fontWeight: 500 }}>Resultado</th>
            <th style={{ padding: `${space.xs} 0`, fontWeight: 500 }}>Referência</th>
          </tr>
        </thead>
        <tbody>
          {exames.map((e) => (
            <tr key={e.nome} style={{ borderTop: `1px solid ${color.border}` }}>
              <td style={{ padding: `${space.xs} 0` }}>{e.nome}</td>
              <td
                style={{
                  padding: `${space.xs} 0`,
                  fontWeight: 600,
                  color: e.foraDaFaixa ? status.yellow.fg : color.text,
                }}
              >
                {e.valor} {e.unidade}
              </td>
              <td style={{ padding: `${space.xs} 0`, color: color.textSecondary }}>
                {e.referencia ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: '12px', color: color.textSecondary, marginTop: space.sm }}>
        A faixa de referência é a do laboratório que fez o exame. Um valor fora dela
        não significa doença — é o seu médico quem interpreta o conjunto.
      </p>
    </section>
  );
}

function Rodape({ fechamento, geradoEm }: { fechamento: string; geradoEm: string }) {
  return (
    <footer
      className="quebra-evitar"
      style={{
        marginTop: space.lg,
        paddingTop: space.md,
        borderTop: `1px solid ${color.border}`,
        fontSize: '12px',
        color: color.textSecondary,
      }}
    >
      <p style={{ margin: `0 0 ${space.sm}`, fontSize: '14px', color: color.text }}>
        {fechamento}
      </p>
      {/* Procedência, pelo mesmo motivo do card de análise: um texto de saúde
          sem origem e sem data é indistinguível de laudo — e não é isso que
          ele é. A revisão médica é dita explicitamente porque é ela que
          separa este documento de um resumo automático. */}
      <p style={{ margin: 0 }}>
        Documento gerado pelo SerBio Health em {geradoEm}, a partir dos dados do seu
        dispositivo e dos exames que você enviou. O texto foi produzido com apoio de
        inteligência artificial e <strong>revisado pelo seu médico</strong> antes de
        chegar até você. É um resumo do período, não um diagnóstico, e não substitui
        a consulta.
      </p>
    </footer>
  );
}
